import {
  getLocaleLanguageAlias,
  mapLocaleAliasToSupportedLocale,
  type MapLocaleToSupportedOptions,
} from '@/utils/i18n/supportedLocales';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

/** Resolve BCP-47 tags for i18n-iso-countries only (not gated by app RTL feature). */
const countryNameLocaleOptions: MapLocaleToSupportedOptions = {
  isRtlLocaleFeatureEnabled: true,
};

countries.registerLocale(en);

const registeredLocales = new Set<string>(['en']);

export async function registerLocale(locale: string): Promise<boolean> {
  const supportedLocale = mapLocaleAliasToSupportedLocale(
    locale,
    countryNameLocaleOptions
  );
  if (!supportedLocale) return false;

  const localeAlias = getLocaleLanguageAlias(supportedLocale);
  if (registeredLocales.has(localeAlias)) return true;

  try {
    const data = (await import(`i18n-iso-countries/langs/${localeAlias}.json`)) as {
      default: countries.LocaleData;
    };
    countries.registerLocale(data.default);
    registeredLocales.add(localeAlias);
    return true;
  } catch {
    console.warn(`Locale ${localeAlias} not available for country names`);
    return false;
  }
}

export const isLocaleRegistered = (locale: string) => {
  const supportedLocale = mapLocaleAliasToSupportedLocale(
    locale,
    countryNameLocaleOptions
  );
  if (!supportedLocale) return registeredLocales.has(locale);

  return registeredLocales.has(getLocaleLanguageAlias(supportedLocale));
};

const intlDisplayNamesCache = new Map<string, Intl.DisplayNames>();

const getIntlDisplayName = (code: string, locale: string): string | undefined => {
  try {
    let displayNames = intlDisplayNamesCache.get(locale);
    if (!displayNames) {
      displayNames = new Intl.DisplayNames([locale], { type: 'region' });
      intlDisplayNamesCache.set(locale, displayNames);
    }

    return displayNames.of(code.toUpperCase()) ?? undefined;
  } catch {
    return undefined;
  }
};

export const getLocalizedCountryName = (code: string, locale: string) => {
  const upper = code.toUpperCase();
  const supportedLocale = mapLocaleAliasToSupportedLocale(
    locale,
    countryNameLocaleOptions
  );
  const localeAlias = supportedLocale ? getLocaleLanguageAlias(supportedLocale) : locale;

  return (
    getIntlDisplayName(upper, locale) ||
    countries.getName(upper, localeAlias) ||
    countries.getName(upper, 'en') ||
    code
  );
};

export const getEnglishCountryName = (code: string) =>
  countries.getName(code.toUpperCase(), 'en') || code;
